import { useMemo } from 'react'
import * as THREE from 'three'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

export interface PropsVStrut3D {
  /** Ponto de apoio da cabeça da escora no veículo, relativo à base no solo */
  para: [number, number, number]
  /** Ponto de ancoragem da cinta no veículo (opcional) */
  pontoAncoragem?: [number, number, number]
  /** Cor de validação/estado do equipamento ('#10b981' correta, '#f59e0b' próxima, etc.) */
  cor?: string
  /** Se o equipamento está selecionado pelo usuário */
  selecionado?: boolean
  /** Escala geral */
  escala?: number
  mostrarFita?: boolean
  tensionada?: boolean
  onClick?: () => void
}

// Cores características do equipamento profissional Holmatro V-Strut
const COR_HOLMATRO_LARANJA = '#ea580c'
const COR_ALUMINIO_HASTE = '#cbd5e1'
const COR_ACO_FORJADO = '#1e293b'
const COR_CATRACA_ZINCO = '#64748b'
const COR_EMPUNHADURA_AMARELA = '#facc15'
const COR_CINTA_POLIESTER = '#f97316'
const COR_MANGA_PROTECAO = '#dc2626'
const COR_GANCHO_ACO = '#475569'

/**
 * Escora Telescópica de Resgate V-Strut (Holmatro V-Strut).
 *
 * Sistema tudo-em-um para estabilização veicular em salvamento veicular técnico.
 * Componentes modelados com precisão:
 * 1. Sapata de base articulada com clevis em U, pino passante e olhal de ancoragem.
 * 2. Corpo estrutural externo retangular em laranja de resgate, com painel lateral preto (sem logo).
 * 3. Carretel integrado retrátil de recolhimento da cinta com flange circular.
 * 4. Catraca de tensionamento integrada com alavanca e empunhadura ergonômica amarela.
 * 5. Colarinho com trava automática ("squeeze and push") no topo do tubo externo.
 * 6. Haste telescópica interna em alumínio estrutural prateado com matriz de orifícios de travamento rápido.
 * 7. Cabeça de apoio multiuso serrilhada com ponta piramidal/chisel, sulco em V e dentes escalonados de mordida.
 * 8. Cinta catraca de alta tenacidade em poliéster com gancho de ancoragem de aço e manga protetora têxtil vermelha anti-abrasão.
 */
export function VStrut3D({
  para,
  pontoAncoragem,
  cor,
  selecionado = false,
  escala = 1,
  onClick,
  mostrarFita = true,
  tensionada = true,
}: PropsVStrut3D) {
  // Altura do pino de articulação sobre a base no solo
  const hPivo = 0.045

  // Vetor do pino de articulação da base até o ponto de contato no veículo
  const { compEfetivo, azimuteRad, quaternion, compTuboExterno, compHasteTotal, furosTravamento, ancoraCintaLocal } =
    useMemo(() => {
      const delta = new THREE.Vector3(para[0], Math.max(0.2, para[1] - hPivo), para[2])
      const comp = Math.max(0.45, delta.length())
      // Vetor unitário ao longo do eixo da haste
      const vY = delta.clone().normalize()
      const azimute = Math.atan2(para[0], para[2])

      // Direção horizontal para onde a escora se projeta (em direção ao veículo)
      const dirH = new THREE.Vector3(para[0], 0, para[2])
      if (dirH.lengthSq() < 0.0001) {
        dirH.set(0, 0, -1)
      } else {
        dirH.normalize()
      }

      // Eixo transversal da articulação (paralelo ao pino da sapata)
      const vX = new THREE.Vector3().crossVectors(dirH, new THREE.Vector3(0, 1, 0)).normalize()
      // Eixo normal da face externa (aponta para longe do veículo / cima, onde fica a catraca)
      const vZ = new THREE.Vector3().crossVectors(vX, vY).normalize()

      const mat = new THREE.Matrix4().makeBasis(vX, vY, vZ)
      const q = new THREE.Quaternion().setFromRotationMatrix(mat)

      // Tubo externo ocupa ~55-60% do vão total
      const lExt = Math.min(Math.max(comp * 0.56, 0.55), comp - 0.22)
      const lVisivelHaste = comp - lExt
      const lHasteTotal = lVisivelHaste + 0.18

      // Orifícios de travamento rápido ao longo da haste interna
      const furos: number[] = []
      const passoFuro = 0.04
      const numFuros = Math.floor((lVisivelHaste - 0.08) / passoFuro)
      for (let i = 0; i < numFuros; i++) {
        furos.push(lExt + 0.05 + i * passoFuro)
      }

      // Ponto de ancoragem da cinta no chassi/soleira do veículo (na base do veículo sob a cabeça)
      const ancora: [number, number, number] = pontoAncoragem ?? [
        para[0],
        0.14,
        para[2],
      ]

      return {
        compEfetivo: comp,
        azimuteRad: azimute,
        quaternion: q,
        compTuboExterno: lExt,
        compHasteTotal: lHasteTotal,
        furosTravamento: furos,
        ancoraCintaLocal: ancora,
      }
    }, [para, pontoAncoragem])

  // Ponto da base de onde sai a cinta tensionada
  const pinoCintaBase: [number, number, number] = useMemo(() => {
    // Projeção horizontal do olhal frontal da sapata de base
    const offsetZ = 0.12
    return [Math.sin(azimuteRad) * offsetZ, 0.035, Math.cos(azimuteRad) * offsetZ]
  }, [azimuteRad])

  return (
    <group scale={escala} onClick={onClick}>
      {/* ================================================================= */}
      {/* 1. SAPATA DE APOIO NO SOLO (Pé Articulado)                         */}
      {/* ================================================================= */}
      <group position={[0, 0, 0]} rotation={[0, azimuteRad, 0]}>
        {/* Placa de base em aço galvanizado com bordas chanfradas */}
        <mesh position={[0, 0.007, 0.02]}>
          <CaixaArredondada args={[0.24, 0.014, 0.24]} />
          <meshStandardMaterial color={cor ?? '#475569'} metalness={0.82} roughness={0.32} />
        </mesh>

        {/* Furos de fixação com estacas/pinos de solo nos cantos */}
        {[-0.09, 0.09].map((x) =>
          [-0.07, 0.11].map((z) => (
            <mesh key={`${x}-${z}`} position={[x, 0.015, z]}>
              <cylinderGeometry args={[0.009, 0.009, 0.006, 12]} />
              <meshStandardMaterial color="#0f172a" roughness={0.9} />
            </mesh>
          )),
        )}

        {/* Garras / nervuras antiderrapantes sob a base */}
        {[-0.08, 0, 0.08].map((x) => (
          <mesh key={x} position={[x, 0.001, 0.02]}>
            <CaixaArredondada args={[0.012, 0.004, 0.22]} />
            <meshStandardMaterial color="#1e293b" roughness={0.9} />
          </mesh>
        ))}

        {/* Suporte articulado em U (Clevis com abas verticais) */}
        {[-0.048, 0.048].map((x, i) => (
          <mesh key={i} position={[x, 0.045, 0]}>
            <CaixaArredondada args={[0.01, 0.075, 0.085]} />
            <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.78} roughness={0.35} />
          </mesh>
        ))}

        {/* Pino horizontal de articulação com porca sextavada */}
        <mesh position={[0, hPivo, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.013, 0.013, 0.12, 16]} />
          <meshStandardMaterial color="#94a3b8" metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0.062, hPivo, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.018, 0.018, 0.012, 6]} />
          <meshStandardMaterial color="#64748b" metalness={0.85} roughness={0.3} />
        </mesh>

        {/* Olhal frontal de fixação da cinta com gancho da base */}
        <group position={[0, 0.025, 0.11]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.02, 0.005, 8, 16, Math.PI]} />
            <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.8} roughness={0.3} />
          </mesh>
          {/* Mosquetão / manilha de aço engatado no olhal */}
          <mesh position={[0, 0.012, 0.015]} rotation={[0, 0, Math.PI / 4]}>
            <torusGeometry args={[0.018, 0.004, 6, 16]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.88} roughness={0.25} />
          </mesh>
        </group>
      </group>

      {/* ================================================================= */}
      {/* 2. CONJUNTO TELESCÓPICO ORIENTADO AO LONGO DO VETOR DA ESCORA     */}
      {/* ================================================================= */}
      <group position={[0, hPivo, 0]} quaternion={[quaternion.x, quaternion.y, quaternion.z, quaternion.w]}>
        {/* --------------------------------------------------------------- */}
        {/* CORPO EXTERNO RETANGULAR EM LARANJA HOLMATRO                    */}
        {/* --------------------------------------------------------------- */}
        <group>
          {/* Tubo estrutural principal */}
          <mesh position={[0, compTuboExterno / 2, 0]}>
            <CaixaArredondada args={[0.082, compTuboExterno, 0.062]} />
            <meshStandardMaterial
              color={COR_HOLMATRO_LARANJA}
              roughness={0.38}
              metalness={0.22}
            />
          </mesh>

          {/* Painel preto lateral, sem marca: só o contraste com o laranja */}
          <mesh position={[0, compTuboExterno * 0.45, 0.0315]}>
            <CaixaArredondada args={[0.068, compTuboExterno * 0.45, 0.002]} />
            <meshStandardMaterial color="#0f172a" roughness={0.7} />
          </mesh>

          {/* Listras de advertência e segurança (amarelo/preto) próximas ao topo */}
          <mesh position={[0, compTuboExterno - 0.06, 0.0315]}>
            <CaixaArredondada args={[0.076, 0.038, 0.002]} />
            <meshStandardMaterial color="#eab308" roughness={0.4} />
          </mesh>
          <mesh position={[0, compTuboExterno - 0.06, -0.0315]}>
            <CaixaArredondada args={[0.076, 0.038, 0.002]} />
            <meshStandardMaterial color="#eab308" roughness={0.4} />
          </mesh>
        </group>

        {/* --------------------------------------------------------------- */}
        {/* CARRETEL INTEGRADO DA CINTA (Base do tubo laranja)               */}
        {/* --------------------------------------------------------------- */}
        <group position={[0, 0.11, 0.048]}>
          {/* Flanges circulares laterais do carretel */}
          {[-0.038, 0.038].map((x, idx) => (
            <mesh key={idx} position={[x, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.046, 0.046, 0.006, 24]} />
              <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.75} roughness={0.35} />
            </mesh>
          ))}
          {/* Rolo de cinta de poliéster laranja bobinada no interior */}
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.04, 0.04, 0.07, 24]} />
            <meshStandardMaterial color={COR_CINTA_POLIESTER} roughness={0.82} />
          </mesh>
          {/* Eixo central e tampa retrátil */}
          <mesh position={[0.042, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.016, 0.016, 0.008, 16]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.85} roughness={0.25} />
          </mesh>
        </group>

        {/* --------------------------------------------------------------- */}
        {/* CATRACA DE TENSIONAMENTO INTEGRADA COM EMPUNHADURA AMARELA       */}
        {/* --------------------------------------------------------------- */}
        <group position={[0, Math.min(0.42, compTuboExterno * 0.58), 0.045]}>
          {/* Chassis de aço e placas laterais da catraca */}
          <mesh position={[0, 0, 0]}>
            <CaixaArredondada args={[0.065, 0.095, 0.045]} />
            <meshStandardMaterial color={COR_CATRACA_ZINCO} metalness={0.85} roughness={0.25} />
          </mesh>
          {/* Rodas dentadas laterais da catraca (engrenagens) */}
          {[-0.035, 0.035].map((x, i) => (
            <mesh key={i} position={[x, -0.01, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.026, 0.026, 0.006, 14]} />
              <meshStandardMaterial color="#475569" metalness={0.9} roughness={0.2} />
            </mesh>
          ))}
          {/* Alavanca de aço da catraca projetada para fora */}
          <group position={[0, 0.02, 0.025]} rotation={[0.42, 0, 0]}>
            <mesh position={[0, 0.065, 0]}>
              <CaixaArredondada args={[0.018, 0.13, 0.016]} />
              <meshStandardMaterial color="#94a3b8" metalness={0.88} roughness={0.22} />
            </mesh>
            {/* EMPUNHADURA ERGONÔMICA AMARELA (destaque idêntico à foto) */}
            <mesh position={[0, 0.095, 0]}>
              <CaixaArredondada args={[0.028, 0.075, 0.026]} />
              <meshStandardMaterial
                color={COR_EMPUNHADURA_AMARELA}
                roughness={0.52}
                metalness={0.15}
              />
            </mesh>
            {/* Ranhuras antiderrapantes pretas na empunhadura */}
            {[-0.02, 0, 0.02].map((yOffset, i) => (
              <mesh key={i} position={[0, 0.095 + yOffset, 0.0135]}>
                <CaixaArredondada args={[0.024, 0.006, 0.002]} />
                <meshStandardMaterial color="#1e293b" roughness={0.9} />
              </mesh>
            ))}
          </group>
        </group>

        {/* --------------------------------------------------------------- */}
        {/* COLARINHO DE TRAVAMENTO AUTOMÁTICO (Squeeze & Push)             */}
        {/* --------------------------------------------------------------- */}
        <group position={[0, compTuboExterno, 0]}>
          <mesh position={[0, 0, 0]}>
            <CaixaArredondada args={[0.094, 0.055, 0.074]} />
            <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.75} roughness={0.35} />
          </mesh>
          {/* Botões / gatilhos de liberação rápida nas laterais */}
          {[-0.049, 0.049].map((x, i) => (
            <mesh key={i} position={[x, 0, 0]}>
              <CaixaArredondada args={[0.012, 0.028, 0.022]} />
              <meshStandardMaterial color="#dc2626" roughness={0.4} />
            </mesh>
          ))}
        </group>

        {/* --------------------------------------------------------------- */}
        {/* HASTE TELESCÓPICA INTERNA EM ALUMÍNIO PRATEADO PERFURADO        */}
        {/* --------------------------------------------------------------- */}
        <group>
          {/* Perfil retangular de alumínio estrutural */}
          <mesh position={[0, compTuboExterno + (compEfetivo - compTuboExterno) / 2 - 0.03, 0]}>
            <CaixaArredondada args={[0.058, compHasteTotal, 0.044]} />
            <meshStandardMaterial
              color={COR_ALUMINIO_HASTE}
              metalness={0.88}
              roughness={0.22}
            />
          </mesh>

          {/* Orifícios retangulares de travamento fino ao longo da face frontal */}
          {furosTravamento.map((yFuro, idx) => (
            <group key={idx} position={[0, yFuro, 0]}>
              <mesh position={[0, 0, 0.0224]}>
                <CaixaArredondada args={[0.025, 0.016, 0.002]} />
                <meshStandardMaterial color="#0f172a" roughness={0.95} />
              </mesh>
              <mesh position={[0, 0, -0.0224]}>
                <CaixaArredondada args={[0.025, 0.016, 0.002]} />
                <meshStandardMaterial color="#0f172a" roughness={0.95} />
              </mesh>
            </group>
          ))}
        </group>

        {/* --------------------------------------------------------------- */}
        {/* CABEÇA DE APOIO MULTIUSO SERRILHADA (Grip Head de Resgate)       */}
        {/* --------------------------------------------------------------- */}
        <group position={[0, compEfetivo, 0]}>
          {/* Colarinho superior de fixação da cabeça */}
          <mesh position={[0, -0.018, 0]}>
            <CaixaArredondada args={[0.068, 0.036, 0.052]} />
            <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.78} roughness={0.35} />
          </mesh>

          {/* Bloco maciço chanfrado da cabeça dentada */}
          <mesh position={[0, 0.012, 0]}>
            <CaixaArredondada args={[0.082, 0.032, 0.062]} />
            <meshStandardMaterial color={COR_ACO_FORJADO} metalness={0.82} roughness={0.3} />
          </mesh>

          {/* Ponta central piramidal (Chisel / ponta para furos de chassi) */}
          <mesh position={[0, 0.038, 0]} rotation={[0, Math.PI / 4, 0]}>
            <coneGeometry args={[0.022, 0.038, 4]} />
            <meshStandardMaterial color="#475569" metalness={0.85} roughness={0.25} />
          </mesh>

          {/* Dentes serrilhados laterais para mordida em chapas e cantos */}
          {[-0.032, 0.032].map((x, idx) => (
            <group key={idx} position={[x, 0.025, 0]}>
              {[-0.015, 0, 0.015].map((zOffset, toothIdx) => (
                <mesh key={toothIdx} position={[0, 0, zOffset]} rotation={[Math.PI, 0, 0]}>
                  <coneGeometry args={[0.007, 0.016, 3]} />
                  <meshStandardMaterial color="#64748b" metalness={0.88} roughness={0.25} />
                </mesh>
              ))}
            </group>
          ))}

          {/* Sulco em V (entalhe para apoiar soleiras e colunas tubulares) */}
          <mesh position={[0, 0.028, 0]}>
            <CaixaArredondada args={[0.03, 0.008, 0.064]} />
            <meshStandardMaterial color="#0f172a" roughness={0.9} />
          </mesh>
        </group>
      </group>

      {/* ================================================================= */}
      {/* 3. SISTEMA DE CINTA CATRACA INTEGRADA COM GANCHO E MANGA         */}
      {/* ================================================================= */}
      {mostrarFita && <group name={tensionada ? "cinta-vstrut-tensionada" : "cinta-vstrut-solta"}>
        {/* Trecho principal da cinta: da base até o ponto de ancoragem no veículo */}
        {tensionada ? <FitaTensionada
          de={pinoCintaBase}
          para={ancoraCintaLocal}
          largura={0.045}
          espessura={0.004}
          cor={COR_CINTA_POLIESTER}
        /> : <><FitaTensionada de={pinoCintaBase} para={[(pinoCintaBase[0] + ancoraCintaLocal[0]) / 2, Math.max(0.03, ancoraCintaLocal[1] / 2 - 0.12), (pinoCintaBase[2] + ancoraCintaLocal[2]) / 2]} /><FitaTensionada de={[(pinoCintaBase[0] + ancoraCintaLocal[0]) / 2, Math.max(0.03, ancoraCintaLocal[1] / 2 - 0.12), (pinoCintaBase[2] + ancoraCintaLocal[2]) / 2]} para={ancoraCintaLocal} /></>}

        {/* MANGA PROTETORA TÊXTIL VERMELHA (anti-abrasão contra quinas da carroceria) */}
        <MangaProtetora de={pinoCintaBase} para={ancoraCintaLocal} />

        {/* GANCHO DE ANCORAGEM DE AÇO FORJADO (Snap hook preso ao veículo) */}
        <group position={ancoraCintaLocal}>
          <mesh rotation={[0, 0, Math.PI / 3]}>
            <torusGeometry args={[0.024, 0.007, 8, 16, Math.PI * 1.3]} />
            <meshStandardMaterial color={COR_GANCHO_ACO} metalness={0.9} roughness={0.25} />
          </mesh>
          <mesh position={[0.016, 0.022, 0]}>
            <cylinderGeometry args={[0.006, 0.008, 0.022, 8]} />
            <meshStandardMaterial color="#cbd5e1" metalness={0.92} roughness={0.2} />
          </mesh>
        </group>
      </group>}

      {/* ================================================================= */}
      {/* 4. REALCE VISUAL DE SELEÇÃO OU STATUS DE VALIDAÇÃO DIDÁTICA        */}
      {/* ================================================================= */}
      {selecionado && (
        <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.18, 0.22, 32]} />
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.7} />
        </mesh>
      )}

      {cor && (
        <mesh position={[0, 0.003, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.22, 0.25, 32]} />
          <meshBasicMaterial color={cor} transparent opacity={0.8} />
        </mesh>
      )}
    </group>
  )
}

/** Segmento de cinta têxtil tensionada 3D entre dois pontos no espaço. */
export function FitaTensionada({
  de,
  para,
  largura = 0.045,
  espessura = 0.004,
  cor = COR_CINTA_POLIESTER,
}: {
  de: [number, number, number]
  para: [number, number, number]
  largura?: number
  espessura?: number
  cor?: string
}) {
  const delta = useMemo(() => new THREE.Vector3(para[0] - de[0], para[1] - de[1], para[2] - de[2]), [de, para])
  const comp = delta.length()
  const q = useMemo(() => {
    if (comp < 0.001) return new THREE.Quaternion()
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.clone().normalize())
  }, [delta, comp])

  if (comp < 0.001) return null

  const meio: [number, number, number] = [
    (de[0] + para[0]) / 2,
    (de[1] + para[1]) / 2,
    (de[2] + para[2]) / 2,
  ]

  return (
    <mesh position={meio} quaternion={[q.x, q.y, q.z, q.w]}>
      <CaixaArredondada args={[largura, comp, espessura]} />
      <meshStandardMaterial color={cor} roughness={0.78} />
    </mesh>
  )
}

/** Manga têxtil de proteção anti-abrasão deslizada sobre a cinta no ponto de apoio. */
function MangaProtetora({
  de,
  para,
  posicaoRelativa = 0.8,
  comprimento = 0.22,
}: {
  de: [number, number, number]
  para: [number, number, number]
  posicaoRelativa?: number
  comprimento?: number
}) {
  const delta = useMemo(() => new THREE.Vector3(para[0] - de[0], para[1] - de[1], para[2] - de[2]), [de, para])
  const comp = delta.length()
  const q = useMemo(() => {
    if (comp < 0.001) return new THREE.Quaternion()
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.clone().normalize())
  }, [delta, comp])

  if (comp < 0.001) return null

  const pos: [number, number, number] = [
    de[0] + delta.x * posicaoRelativa,
    de[1] + delta.y * posicaoRelativa,
    de[2] + delta.z * posicaoRelativa,
  ]

  return (
    <mesh position={pos} quaternion={[q.x, q.y, q.z, q.w]}>
      <CaixaArredondada args={[0.052, comprimento, 0.016]} />
      <meshStandardMaterial color={COR_MANGA_PROTECAO} roughness={0.85} />
    </mesh>
  )
}



